import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"

const log = logger("DeepDiveEventBus")

export enum DeepDiveMode {
  ALL_METRICS = "ALL_METRICS",
  SELECTED_METRIC = "SELECTED_METRIC",
}

export const EVENTS = {
  MODE_CHANGE: "MODE_CHANGE",
  AUDIO_PLAY: "AUDIO_PLAY",
  AUDIO_PAUSE: "AUDIO_PAUSE",
  AUDIO_TOGGLE: "AUDIO_TOGGLE",
  AUDIO_SEEK: "AUDIO_SEEK",
  AUDIO_TIME: "AUDIO_TIME",
  AUDIO_REGIONS_HOVER: "AUDIO_REGIONS_HOVER",
  MESSAGE_SELECTED: "MESSAGE_SELECTED",
  MESSAGES_HOVER: "MESSAGES_HOVER",
  TEST_DETAILS_CHANGE: "TEST_DETAILS_CHANGE",
} as const

export type EventType = (typeof EVENTS)[keyof typeof EVENTS]

export interface ModeChangePayload {
  type: DeepDiveMode
  data: string
}

export interface AudioPlayPayload {
  time?: number
}

export interface AudioPausePayload {
  time?: number
}

export type AudioTogglePayload = Record<string, never>

export interface AudioSeekPayload {
  time: number
}

// Broadcast playback position so deep-dive surfaces (plots, transcript) can track it.
export interface AudioTimePayload {
  time: number
}

export interface AudioRegionsHoverPayload {
  regions: AudioRegionParams[] | null
}

export interface MessageSelectedPayload {
  messageIndex: number | null
}

export interface MessagesHoverPayload {
  messageIndices: number[] | null
  metricId?: string
}

export type TestDetailsSection =
  | "persona"
  | "testCase"
  | "expectedOutput"
  | "simulationMetadata"
  | "customerMetadata"
  | null

export interface TestDetailsChangePayload {
  showTestDetails: boolean
  activeSection: TestDetailsSection
}

export interface EventPayloads {
  [EVENTS.MODE_CHANGE]: ModeChangePayload
  [EVENTS.AUDIO_PLAY]: AudioPlayPayload
  [EVENTS.AUDIO_PAUSE]: AudioPausePayload
  [EVENTS.AUDIO_TOGGLE]: AudioTogglePayload
  [EVENTS.AUDIO_SEEK]: AudioSeekPayload
  [EVENTS.AUDIO_TIME]: AudioTimePayload
  [EVENTS.AUDIO_REGIONS_HOVER]: AudioRegionsHoverPayload
  [EVENTS.MESSAGE_SELECTED]: MessageSelectedPayload
  [EVENTS.MESSAGES_HOVER]: MessagesHoverPayload
  [EVENTS.TEST_DETAILS_CHANGE]: TestDetailsChangePayload
}

export type EventListener<T extends EventType> = (
  payload: EventPayloads[T],
) => void

class EventBus {
  private listeners: Map<EventType, Set<EventListener<EventType>>> = new Map()

  subscribe<T extends EventType>(
    eventType: T,
    listener: EventListener<T>,
  ): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set())
    }

    this.listeners.get(eventType)!.add(listener as EventListener<EventType>)

    return () => {
      const eventListeners = this.listeners.get(eventType) as
        | Set<EventListener<T>>
        | undefined
      if (eventListeners) {
        eventListeners.delete(listener)
        if (eventListeners.size === 0) {
          this.listeners.delete(eventType)
        }
      }
    }
  }

  dispatch<T extends EventType>(eventType: T, payload: EventPayloads[T]): void {
    const eventListeners = this.listeners.get(eventType) as
      | Set<EventListener<T>>
      | undefined
    if (eventListeners) {
      eventListeners.forEach((listener) => {
        try {
          listener(payload)
        } catch (error) {
          log.error(`Error in event listener for ${eventType}`, error)
        }
      })
    }
  }

  removeAllListeners(eventType: EventType): void {
    this.listeners.delete(eventType)
  }

  clear(): void {
    this.listeners.clear()
  }
}

export const simulationDeepDiveEventBus = new EventBus()
